import React, { useEffect, useMemo, useState } from "react";
import "./AddPointsModal.css";
import { Modal, Select, Form } from "antd";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../store/adminData/adminData";
import useEvents from "../../../../content/Events/useEvents";
import logger from "../../../../../../utils/logger";
import useMembersData from "../../../Members/useMembersData";
import ChantCheckbox from "../../../../../../CommonComponents/ChantCheckbox/ChantCheckbox";
import ChantButton from "../../../../../../CommonComponents/ChantButton/ChantButton";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../../../services/firebaseService/connection";
import {
  updateUserCheckInPoints,
  updateUserVolunteerPoints,
} from "../../../../../../services/firebaseService/endPoints/admin/members/leaderboard";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import ChantSpinner from "../../../../../../CommonComponents/ChantSpinner/ChantSpinner";

const AddPointsModal = ({
  showPointsModal,
  handleClosePointsModal,
  fetchEvents,
}) => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [form] = Form.useForm();

  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Checkin points
  const [checkInChecked, setCheckInChecked] = useState(false); // checkbox state
  const [checkInPoints, setCheckInPoints] = useState(0); // current points state
  const [initialCheckInPoints, setInitialCheckInPoints] = useState(0); // initial points state

  // Volunteer points
  const [volunteerPointsChecked, setVolunteerPointsChecked] = useState({});
  const [volunteerPoints, setVolunteerPoints] = useState({});
  const [initialVolunteerPoints, setInitialVolunteerPoints] = useState({});

  const handleCancel = () => {
    // Reset form and state
    form.resetFields();
    resetState();

    // Close modal
    handleClosePointsModal();
  };

  const { fetchEventsBySeason } = useEvents();

  const { membersList, membersLoading } = useMembersData();

  const fetchAllEvents = async () => {
    try {
      setEventsLoading(true);
      const currentYear = new Date().getFullYear();
      const previousYear = new Date().getFullYear() - 1;

      const currentYearData = await fetchEventsBySeason(
        groupData.id,
        currentYear
      );
      const previousYearData = await fetchEventsBySeason(
        groupData.id,
        previousYear
      );

      const data = [...currentYearData, ...previousYearData];

      const sortedEvents = data.sort((a, b) => {
        return dayjs(b?.details?.eventStartDate)?.diff(
          dayjs(a?.details?.eventStartDate)
        );
      });

      setEvents(sortedEvents);
    } catch (error) {
      logger.error("Failed to fetch events");
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchUserPoints = async (memberId) => {
    try {
      if (
        !groupData?.clubId ||
        !groupData?.id ||
        !selectedEventId ||
        !memberId
      ) {
        logger.error("Missing required data for fetching user details");
        return [null, null];
      }

      const currentSeason = selectedEvent?.checkIn?.season?.toString();

      // Build the path to the user's document in the users subcollection
      const userDocRef = doc(
        db,
        "leaderBoard",
        groupData.clubId,
        "groups",
        groupData.id,
        "eventLeaderBoard",
        currentSeason,
        "users",
        memberId
      );

      // Fetch the user document
      const userDocSnap = await getDoc(userDocRef);

      let eventData = null;

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();

        // Access the eventSummary object
        const eventSummary = userData.eventSummary || {};

        // Access the specific event using the eventId as key
        eventData = eventSummary[selectedEventId];
      } else {
        logger.log("No user document found for this member");
      }

      // Now fetch the volunteer data from the specified path
      const volunteerDocRef = doc(
        db,
        "groupAdminPost",
        groupData.id,
        "posts",
        selectedEventId,
        "rsvp",
        memberId
      );

      // Fetch the volunteer document
      const volunteerDocSnap = await getDoc(volunteerDocRef);

      let volunteerData = null;

      if (volunteerDocSnap.exists()) {
        const volunteerDocData = volunteerDocSnap.data();

        // Get the volunteered object if it exists
        volunteerData = volunteerDocData.volunteered || null;
      } else {
        logger.log("No volunteer data found for this member");
      }

      return [eventData, volunteerData];
    } catch (error) {
      logger.error("Failed to fetch user details", error);
      return [null, null];
    }
  };

  useEffect(() => {
    if (groupData?.id) {
      fetchAllEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const resetCheckInPoints = () => {
    setCheckInChecked(false);
    setCheckInPoints(0);
    setInitialCheckInPoints(0);
  };

  const resetVolunteerPoints = () => {
    setVolunteerPointsChecked({});
    setVolunteerPoints({});
    setInitialVolunteerPoints({});
  };

  const handleEventChange = (value) => {
    setSelectedEventId(value);

    const selectedEvent = events.find((event) => event.id === value);
    setSelectedEvent(selectedEvent);

    if (selectedEvent?.additionalLocations?.length === 1) {
      setSelectedLocationId(selectedEvent?.additionalLocations[0]?.placeId);
      setSelectedLocationDetails({
        address: selectedEvent?.additionalLocations[0]?.address,
        locationIndex: 0,
        locationId: selectedEvent?.additionalLocations[0]?.locationId,
      });
    }

    // Clear member selection when event changes
    setSelectedMemberId(null);
    form.setFieldsValue({ memberId: undefined });
    // Reset points state
    resetCheckInPoints();
    resetVolunteerPoints();
  };

  const handleVolunteerPointsChange = (
    slotId,
    slotTitle,
    slotPoints,
    checked
  ) => {
    // Convert points to number
    const pointsAsNumber = Number(slotPoints);

    setVolunteerPointsChecked((prev) => ({
      ...prev,
      [slotId]: checked,
    }));

    setVolunteerPoints((prev) => ({
      ...prev,
      [slotId]: {
        title: slotTitle,
        points: checked ? pointsAsNumber : 0,
      },
    }));
  };

  const [pointsLoading, setPointsLoading] = useState(false);

  const resetState = () => {
    // setSelectedEventId(null);
    // setSelectedEvent(null);
    setSelectedMemberId(null);
    form.setFieldsValue({ memberId: undefined });
    resetCheckInPoints();
    resetVolunteerPoints();
  };

  const finalMembersList = useMemo(
    () => membersList?.filter((member) => member?.registeredOnChantDate > 0),
    [membersList]
  );

  const handleMemberChange = async (value) => {
    try {
      setPointsLoading(true);
      setSelectedMemberId(value);

      // Reset existing points state
      resetCheckInPoints();
      resetVolunteerPoints();

      // Fetch user points data
      const [eventData, volunteerData] = await fetchUserPoints(value);

      // Set check-in points if they exist
      if (
        eventData &&
        eventData.checkInPoint &&
        Number(eventData.checkInPoint) > 0
      ) {
        setCheckInChecked(true);
        setCheckInPoints(Number(eventData.checkInPoint));
        setInitialCheckInPoints(Number(eventData.checkInPoint));
      }

      // Set volunteer points if they exist
      if (volunteerData && selectedEvent?.volunteerRequests) {
        // Initialize volunteer points based on data
        Object.entries(selectedEvent?.volunteerRequests).forEach(
          ([slotId, slot]) => {
            const slotPoints = Number(slot.points);

            setVolunteerPoints((prev) => ({
              ...prev,
              [slotId]: {
                title: slot.title,
                points: volunteerData[slotId] ? slotPoints : 0,
              },
            }));
            setInitialVolunteerPoints((prev) => ({
              ...prev,
              [slotId]: volunteerData[slotId] ? slotPoints : 0,
            }));

            setVolunteerPointsChecked((prev) => ({
              ...prev,
              [slotId]: volunteerData[slotId] || false,
            }));
          }
        );
      }
    } catch (error) {
      toast.error("Failed to fetch user points");
    } finally {
      setPointsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitLoading(true);
      const { eventId, memberId } = values;

      // Convert to strings for comparison
      const checkInPointsChanged =
        Number(checkInPoints) !== Number(initialCheckInPoints);

      let uniqueVolunteerPoints = {};

      Object.keys(volunteerPoints).forEach((slotId) => {
        const currentPoints = Number(volunteerPoints[slotId].points);
        const initialPoints = Number(initialVolunteerPoints[slotId] || 0);

        if (currentPoints !== initialPoints) {
          uniqueVolunteerPoints[slotId] = {
            title: volunteerPoints[slotId].title,
            points: currentPoints,
          };
        }
      });

      const volunteerPointsChanged =
        Object.keys(uniqueVolunteerPoints).length > 0;

      if (!checkInPointsChanged && !volunteerPointsChanged) {
        toast.error("No points to update");
        return;
      }

      const selectedMember = finalMembersList.find(
        (member) => member.id === memberId
      );

      const eventDetails = events.find((event) => event.id === eventId);

      if (checkInPointsChanged) {
        await updateUserCheckInPoints(
          groupData.id,
          groupData.clubId,
          memberId,
          eventId,
          eventDetails?.checkIn?.season.toString(),
          selectedMember?.fullName,
          selectedMember?.userRole,
          eventDetails,
          selectedLocationDetails,
          checkInPoints,
          initialCheckInPoints
        );
      }

      if (volunteerPointsChanged) {
        await updateUserVolunteerPoints(
          groupData.id,
          groupData.clubId,
          memberId,
          eventId,
          eventDetails?.checkIn?.season.toString(),
          selectedMember?.fullName,
          selectedLocationDetails,
          initialVolunteerPoints,
          volunteerPoints
        );
      }

      handleMemberChange(memberId);
      toast.success("Points updated successfully");

      // Reset everything before closing
      // form.resetFields();
      resetState();
      // Trigger parent callbacks
      fetchEvents();
      // handleClosePointsModal();
    } catch {
      logger.error("Error updating points:");
      toast.error("Failed to update user points");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCheckInPointsChange = (checked, points) => {
    setCheckInChecked(checked);
    setCheckInPoints(checked ? Number(points) : 0);
  };

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState(null);

  const handleLocationChange = (value) => {
    setSelectedLocationId(value);

    const selectedLocationIndex = selectedEvent?.additionalLocations.findIndex(
      (location) => location.placeId === value
    );

    if (selectedLocationIndex !== -1) {
      const selectedLocation = {
        address:
          selectedEvent?.additionalLocations[selectedLocationIndex]?.address,
        locationIndex: selectedLocationIndex,
        locationId:
          selectedEvent?.additionalLocations[selectedLocationIndex]?.locationId,
      };

      setSelectedLocationDetails(selectedLocation);
    }
  };

  return (
    <Modal
      title="Manage Points"
      open={showPointsModal}
      onCancel={handleCancel}
      width={700}
      footer={null}
      className="add-points-modal"
    >
      <div className="add-points-container">
        <h6>
          Select the event and member to add missing event and volunteer points.
        </h6>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="form-content">
            <Form.Item
              name="eventId"
              label="Select Event"
              rules={[{ required: true, message: "Please select an event" }]}
            >
              <Select
                showSearch
                id="event-select"
                placeholder="Select an event"
                loading={eventsLoading || submitLoading}
                onChange={handleEventChange}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={events?.map((event) => {
                  const displayName =
                    event.title || event.name || `[No Event Name]`;

                  let dateStr = "";
                  if (event?.details?.eventStartDate) {
                    dateStr = ` (${dayjs.unix(event.details.eventStartDate).format("MMM DD, YYYY")})`;
                  }

                  return {
                    value: event.id,
                    label: event.details?.eventStartDate
                      ? `${displayName}${dateStr}`
                      : displayName,
                  };
                })}
              />
            </Form.Item>

            {selectedEvent?.additionalLocations?.length > 1 && (
              <Form.Item
                name="eventLocation"
                label="Select Location"
                rules={[
                  { required: true, message: "Please select a location" },
                ]}
              >
                <Select
                  disabled={!selectedEventId || submitLoading}
                  id="location-select"
                  value={selectedLocationId}
                  placeholder="Select a location"
                  onChange={handleLocationChange}
                  options={selectedEvent?.additionalLocations?.map(
                    (location) => ({
                      value: location.placeId,
                      label: location.name || location.address,
                    })
                  )}
                />
              </Form.Item>
            )}

            <Form.Item
              name="memberId"
              label="Search Member"
              rules={[{ required: true, message: "Please select a member" }]}
            >
              <Select
                showSearch
                disabled={!selectedEventId || submitLoading}
                id="member-select"
                value={selectedMemberId}
                placeholder="Select a member"
                loading={membersLoading}
                onChange={handleMemberChange}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={finalMembersList?.map((member) => ({
                  value: member.id,
                  label: member.name || `Member #${member.id}`,
                }))}
              />
            </Form.Item>

            {pointsLoading ? (
              <ChantSpinner isScreenLoader />
            ) : (
              <div>
                {selectedMemberId &&
                  selectedEvent?.checkIn?.points &&
                  selectedEvent?.checkIn?.points?.length > 0 && (
                    <Form.Item label="Event Attendance">
                      <div style={{ display: "flex", gap: "6px" }}>
                        <ChantCheckbox
                          disabled={submitLoading}
                          onChange={(e) =>
                            handleCheckInPointsChange(
                              e.target.checked,
                              selectedEvent?.checkIn?.points
                            )
                          }
                          checked={checkInChecked}
                        />
                        {`${selectedEvent?.checkIn?.points} points for check-in`}
                      </div>
                    </Form.Item>
                  )}

                {selectedMemberId &&
                  selectedEvent?.volunteerRequests &&
                  Object.keys(selectedEvent?.volunteerRequests)?.length > 0 && (
                    <Form.Item
                      label="Volunteering"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {Object.entries(selectedEvent?.volunteerRequests)?.map(
                        ([slotId, slot]) => (
                          <div
                            key={slotId}
                            style={{ display: "flex", gap: "6px" }}
                          >
                            <ChantCheckbox
                              disabled={submitLoading}
                              onChange={(e) =>
                                handleVolunteerPointsChange(
                                  slotId,
                                  slot.title,
                                  slot.points,
                                  e.target.checked
                                )
                              }
                              checked={volunteerPointsChecked[slotId]}
                            />
                            {`${slot?.title} (${slot?.points} points) `}
                          </div>
                        )
                      )}
                    </Form.Item>
                  )}
              </div>
            )}
          </div>

          <div className="form-actions">
            <ChantButton
              onClick={handleCancel}
              type="secondary"
              disabled={submitLoading}
            >
              Cancel
            </ChantButton>
            <ChantButton
              type="primary"
              htmlType="submit"
              loading={submitLoading}
              disabled={submitLoading}
            >
              Save
            </ChantButton>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AddPointsModal;
